// Extracted from `gitgraph.ts` because it caused `utils` tests to fail
// because of circular dependency between `utils` and `template`.
// It's not clear why (the circular dependency still exist) but `Orientation`
// was the only one causing issue. Maybe because it's an enum?
var Orientation;
(function (Orientation) {
    Orientation["VerticalReverse"] = "vertical-reverse";
    Orientation["Horizontal"] = "horizontal";
    Orientation["HorizontalReverse"] = "horizontal-reverse";
})(Orientation || (Orientation = {}));

/**
 * Provide a default value to a boolean.
 * @param value
 * @param defaultValue
 */
function booleanOptionOr(value, defaultValue) {
    return typeof value === "boolean" ? value : defaultValue;
}
/**
 * Provide a default value to a number.
 * @param value
 * @param defaultValue
 */
function numberOptionOr(value, defaultValue) {
    return typeof value === "number" ? value : defaultValue;
}
/**
 * Creates an object composed of the picked object properties.
 * @param obj The source object
 * @param paths The property paths to pick
 */
function pick(obj, paths) {
    return {
        ...paths.reduce((mem, key) => ({ ...mem, [key]: obj[key] }), {}),
    };
}
/**
 * Return true if is undefined.
 *
 * @param obj
 */
function isUndefined(obj) {
    return obj === undefined;
}
/**
 * Return a version of the object without any undefined keys.
 *
 * @param obj
 */
function withoutUndefinedKeys(obj = {}) {
    return Object.keys(obj).reduce((mem, key) => isUndefined(obj[key]) ? mem : { ...mem, [key]: obj[key] }, {});
}
/**
 * Return a string ready to use in `svg.path.d` to draw an arrow from params.
 *
 * @param graph Graph context
 * @param parent Parent commit of the target commit
 * @param commit Target commit
 */
function arrowSvgPath(graph, parent, commit) {
    const commitRadius = commit.style.dot.size;
    const size = graph.template.arrow.size;
    const h = commitRadius + graph.template.arrow.offset;
    // Delta between left & right (radian)
    const delta = Math.PI / 7;
    // Alpha angle between parent & commit (radian)
    const alpha = getAlpha(graph, parent, commit);
    // Top
    const x1 = h * Math.cos(alpha);
    const y1 = h * Math.sin(alpha);
    // Bottom right
    const x2 = (h + size) * Math.cos(alpha - delta);
    const y2 = (h + size) * Math.sin(alpha - delta);
    // Bottom center
    const x3 = (h + size / 2) * Math.cos(alpha);
    const y3 = (h + size / 2) * Math.sin(alpha);
    // Bottom left
    const x4 = (h + size) * Math.cos(alpha + delta);
    const y4 = (h + size) * Math.sin(alpha + delta);
    return `M${x1},${y1} L${x2},${y2} Q${x3},${y3} ${x4},${y4} L${x4},${y4}`;
}
function getAlpha(graph, parent, commit) {
    const deltaX = parent.x - commit.x;
    const deltaY = parent.y - commit.y;
    const commitSpacing = graph.template.commit.spacing;
    let alphaY;
    let alphaX;
    // Angle usually start from previous commit Y position:
    //
    // o
    // ↑ ↖ ︎
    // o  |  <-- path is straight until last commit Y position
    // ↑  o
    // | ↗︎
    // o
    //
    // So we can to default to commit spacing.
    // For horizontal orientation => same with commit X position.
    switch (graph.orientation) {
        case Orientation.Horizontal:
            alphaY = deltaY;
            alphaX = -commitSpacing;
            break;
        case Orientation.HorizontalReverse:
            alphaY = deltaY;
            alphaX = commitSpacing;
            break;
        case Orientation.VerticalReverse:
            alphaY = -commitSpacing;
            alphaX = deltaX;
            break;
        default:
            alphaY = commitSpacing;
            alphaX = deltaX;
            break;
    }
    // If commit is distant from its parent, there should be no angle.
    //
    //    o ︎
    //    ↑  <-- arrow is like previous commit was on same X position
    // o  |
    // | /
    // o
    //
    // For horizontal orientation => same with commit Y position.
    if (graph.isVertical) {
        if (Math.abs(deltaY) > commitSpacing)
            alphaX = 0;
    }
    else {
        if (Math.abs(deltaX) > commitSpacing)
            alphaY = 0;
    }
    if (graph.reverseArrow) {
        alphaY *= -1;
        alphaX *= -1;
    }
    return Math.atan2(alphaY, alphaX);
}

/**
 * Branch merge style enum
 */
var MergeStyle;
(function (MergeStyle) {
    MergeStyle["Bezier"] = "bezier";
    MergeStyle["Straight"] = "straight";
})(MergeStyle || (MergeStyle = {}));
const DEFAULT_FONT = "normal 12pt Calibri";
/**
 * Gitgraph template
 *
 * Set of design rules for the rendering.
 */
class Template {
    constructor(options) {
        // Options
        options.branch = options.branch || {};
        options.branch.label = options.branch.label || {};
        options.arrow = options.arrow || {};
        options.commit = options.commit || {};
        options.commit.dot = options.commit.dot || {};
        options.commit.message = options.commit.message || {};
        // One color per column
        this.colors = options.colors || ["#000000"];
        // Branch style
        this.branch = {
            color: options.branch.color,
            lineWidth: options.branch.lineWidth || 2,
            mergeStyle: options.branch.mergeStyle || MergeStyle.Bezier,
            spacing: numberOptionOr(options.branch.spacing, 20),
            label: {
                display: booleanOptionOr(options.branch.label.display, true),
                color: options.branch.label.color || options.commit.color,
                strokeColor: options.branch.label.strokeColor || options.commit.color,
                bgColor: options.branch.label.bgColor || "white",
                font: options.branch.label.font ||
                    options.commit.message.font ||
                    DEFAULT_FONT,
                borderRadius: numberOptionOr(options.branch.label.borderRadius, 10),
            },
        };
        // Arrow style
        this.arrow = {
            size: options.arrow.size || null,
            color: options.arrow.color || null,
            offset: options.arrow.offset || 2,
        };
        // Commit style
        this.commit = {
            color: options.commit.color,
            spacing: numberOptionOr(options.commit.spacing, 25),
            hasTooltipInCompactMode: booleanOptionOr(options.commit.hasTooltipInCompactMode, true),
            dot: {
                color: options.commit.dot.color || options.commit.color,
                size: options.commit.dot.size || 3,
                strokeWidth: numberOptionOr(options.commit.dot.strokeWidth, 0),
                strokeColor: options.commit.dot.strokeColor,
                font: options.commit.dot.font ||
                    options.commit.message.font ||
                    "normal 10pt Calibri",
            },
            message: {
                display: booleanOptionOr(options.commit.message.display, true),
                displayAuthor: booleanOptionOr(options.commit.message.displayAuthor, true),
                displayHash: booleanOptionOr(options.commit.message.displayHash, true),
                color: options.commit.message.color || options.commit.color,
                font: options.commit.message.font || DEFAULT_FONT,
            },
        };
        // Tag style
        // This one is computed in the Tag instance. It needs Commit style
        // that is partially computed at runtime (for colors).
        this.tag = options.tag || {};
    }
}
/**
 * Black arrow template
 */
const blackArrowTemplate = new Template({
    colors: ["#6963FF", "#47E8D4", "#6BDB52", "#E84BA5", "#FFA657"],
    branch: {
        color: "#000000",
        lineWidth: 4,
        spacing: 50,
        mergeStyle: MergeStyle.Straight,
    },
    commit: {
        spacing: 60,
        dot: {
            size: 16,
            strokeColor: "#000000",
            strokeWidth: 4,
        },
        message: {
            color: "black",
        },
    },
    arrow: {
        size: 16,
        offset: -1.5,
    },
});
/**
 * Metro template
 */
const metroTemplate = new Template({
    colors: ["#979797", "#008fb5", "#f1c109"],
    branch: {
        lineWidth: 10,
        spacing: 50,
    },
    commit: {
        spacing: 80,
        dot: {
            size: 14,
        },
        message: {
            font: "normal 14pt Arial",
        },
    },
});
var TemplateName;
(function (TemplateName) {
    TemplateName["Metro"] = "metro";
    TemplateName["BlackArrow"] = "blackarrow";
})(TemplateName || (TemplateName = {}));
/**
 * Extend an existing template with new options.
 *
 * @param selectedTemplate Template to extend
 * @param options Template options
 */
function templateExtend(selectedTemplate, options) {
    const template = getTemplate(selectedTemplate);
    if (!options.branch)
        options.branch = {};
    if (!options.commit)
        options.commit = {};
    // This is tedious, but it seems acceptable so we don't need lodash
    // as we want to keep bundlesize small.
    return {
        colors: options.colors || template.colors,
        arrow: {
            ...template.arrow,
            ...options.arrow,
        },
        branch: {
            ...template.branch,
            ...options.branch,
            label: {
                ...template.branch.label,
                ...options.branch.label,
            },
        },
        commit: {
            ...template.commit,
            ...options.commit,
            dot: {
                ...template.commit.dot,
                ...options.commit.dot,
            },
            message: {
                ...template.commit.message,
                ...options.commit.message,
            },
        },
        tag: {
            ...template.tag,
            ...options.tag,
        },
    };
}
/**
 * Resolve the template to use regarding given `template` value.
 *
 * @param template Selected template name, or instance.
 */
function getTemplate(template) {
    if (!template)
        return metroTemplate;
    if (typeof template === "string") {
        return {
            [TemplateName.BlackArrow]: blackArrowTemplate,
            [TemplateName.Metro]: metroTemplate,
        }[template];
    }
    return template;
}

class Tag {
    /**
     * Style
     */
    get style() {
        return {
            strokeColor: this.tagStyle.strokeColor || this.commitStyle.color,
            bgColor: this.tagStyle.bgColor || this.commitStyle.color,
            color: this.tagStyle.color || "white",
            font: this.tagStyle.font || this.commitStyle.message.font || DEFAULT_FONT,
            borderRadius: numberOptionOr(this.tagStyle.borderRadius, 10),
            pointerWidth: numberOptionOr(this.tagStyle.pointerWidth, 12),
        };
    }
    constructor(name, style, render, commitStyle) {
        this.name = name;
        this.tagStyle = style;
        this.commitStyle = commitStyle;
        this.render = render;
    }
}

/**
 * Generate a random hash.
 *
 * @return hex string with 40 chars
 */
const getRandomHash = () => (Math.random().toString(16).substring(3) +
    Math.random().toString(16).substring(3) +
    Math.random().toString(16).substring(3) +
    Math.random().toString(16).substring(3)).substring(0, 40);
class Commit {
    /**
     * Message
     */
    get message() {
        let message = "";
        if (this.style.message.displayHash) {
            message += `${this.hashAbbrev} `;
        }
        message += this.subject;
        if (this.style.message.displayAuthor) {
            message += ` - ${this.author.name} <${this.author.email}>`;
        }
        return message;
    }
    /**
     * Branch that should be rendered
     */
    get branchToDisplay() {
        return this.branches ? this.branches[0] : "";
    }
    constructor(options) {
        /**
         * Ref names
         */
        this.refs = [];
        /**
         * Commit x position
         */
        this.x = 0;
        /**
         * Commit y position
         */
        this.y = 0;
        // Set author & committer
        let name, email;
        try {
            [, name, email] = options.author.match(/(.*) <(.*)>/);
        }
        catch {
            [name, email] = [options.author, ""];
        }
        this.author = { name, email, timestamp: Date.now() };
        this.committer = { name, email, timestamp: Date.now() };
        // Set commit message
        this.subject = options.subject;
        this.body = options.body || "";
        // Set commit hash
        this.hash = options.hash || getRandomHash();
        this.hashAbbrev = this.hash.substring(0, 7);
        // Set parent hash
        this.parents = options.parents ? options.parents : [];
        this.parentsAbbrev = this.parents.map((commit) => commit.substring(0, 7));
        // Set style
        this.style = {
            ...options.style,
            message: { ...options.style.message },
            dot: { ...options.style.dot },
        };
        this.dotText = options.dotText;
        // Set callbacks
        this.onClick = () => (options.onClick ? options.onClick(this) : undefined);
        this.onMessageClick = () => options.onMessageClick ? options.onMessageClick(this) : undefined;
        this.onMouseOver = () => options.onMouseOver ? options.onMouseOver(this) : undefined;
        this.onMouseOut = () => options.onMouseOut ? options.onMouseOut(this) : undefined;
        // Set custom renders
        this.renderDot = options.renderDot;
        this.renderMessage = options.renderMessage;
        this.renderTooltip = options.renderTooltip;
    }
    setRefs(refs) {
        this.refs = refs.getNames(this.hash);
        return this;
    }
    setTags(tags, getTagStyle, getTagRender) {
        this.tags = tags
            .getNames(this.hash)
            .map((name) => new Tag(name, getTagStyle(name), getTagRender(name), this.style));
        return this;
    }
    setBranches(branches) {
        this.branches = branches;
        return this;
    }
    setPosition({ x, y }) {
        this.x = x;
        this.y = y;
        return this;
    }
    withDefaultColor(color) {
        const newStyle = {
            ...this.style,
            dot: { ...this.style.dot },
            message: { ...this.style.message },
        };
        if (!newStyle.color)
            newStyle.color = color;
        if (!newStyle.dot.color)
            newStyle.dot.color = color;
        if (!newStyle.message.color)
            newStyle.message.color = color;
        const commit = this.cloneCommit();
        commit.style = newStyle;
        return commit;
    }
    /**
     * Ideally, we want Commit to be a [Value Object](https://martinfowler.com/bliki/ValueObject.html).
     * We started with a mutable class. So we'll refactor that little by little.
     * This private function is a helper to create a new Commit from existing one.
     */
    cloneCommit() {
        const commit = new Commit({
            author: `${this.author.name} <${this.author.email}>`,
            subject: this.subject,
            style: this.style,
            body: this.body,
            hash: this.hash,
            parents: this.parents,
            dotText: this.dotText,
            onClick: this.onClick,
            onMessageClick: this.onMessageClick,
            onMouseOver: this.onMouseOver,
            onMouseOut: this.onMouseOut,
            renderDot: this.renderDot,
            renderMessage: this.renderMessage,
            renderTooltip: this.renderTooltip,
        });
        commit.refs = this.refs;
        commit.branches = this.branches;
        commit.tags = this.tags;
        commit.x = this.x;
        commit.y = this.y;
        return commit;
    }
}

class BranchUserApi {
    // tslint:enable:variable-name
    constructor(branch, graph, onGraphUpdate) {
        this._branch = branch;
        this.name = branch.name;
        this._graph = graph;
        this._onGraphUpdate = onGraphUpdate;
    }
    branch(args) {
        if (this._branch.isDeleted() && !this._isReferenced()) {
            throw new Error(`Cannot branch from the deleted branch "${this.name}"`);
        }
        const options = typeof args === "string" ? { name: args } : args;
        options.from = this;
        return this._graph.createBranch(options).getUserApi();
    }
    commit(options) {
        if (this._branch.isDeleted() && !this._isReferenced()) {
            throw new Error(`Cannot commit on the deleted branch "${this.name}"`);
        }
        // Deal with shorter syntax
        if (typeof options === "string")
            options = { subject: options };
        if (!options)
            options = {};
        this._commitWithParents(options, []);
        this._onGraphUpdate();
        return this;
    }
    /**
     * Delete the branch (as `git branch -d`)
     */
    delete() {
        // Delete all references to the branch from the graph (graph.branches and graph.refs)
        // and from the commits (commit.refs). Then, make the branch instance a deleted branch.
        // Like in git, the commits and tags in the deleted branch remain in the graph
        if (this._graph.refs.getCommit("HEAD") ===
            this._graph.refs.getCommit(this.name)) {
            throw new Error(`Cannot delete the checked out branch "${this.name}"`);
        }
        const branchCommits = (function* (graph, branch) {
            const lookupCommit = (graph, commitHash) => {
                return graph.commits.find(({ hash }) => hash === commitHash);
            };
            let currentCommit = lookupCommit(graph, graph.refs.getCommit(branch.name));
            while (currentCommit && currentCommit.hash !== branch.parentCommitHash) {
                yield currentCommit;
                currentCommit = lookupCommit(graph, currentCommit.parents[0]);
            }
            return;
        })(this._graph, this._branch);
        [...branchCommits].forEach((commit) => {
            commit.refs = commit.refs.filter((branchName) => branchName !== this.name);
        });
        this._graph.refs.delete(this.name);
        this._graph.branches.delete(this.name);
        this._branch = createDeletedBranch(this._graph, this._branch.style, () => {
            // do nothing
        });
        this._onGraphUpdate();
        return this;
    }
    merge(...args) {
        if (this._branch.isDeleted() && !this._isReferenced()) {
            throw new Error(`Cannot merge to the deleted branch "${this.name}"`);
        }
        let options = args[0];
        if (!isBranchMergeOptions(options)) {
            options = {
                branch: args[0],
                fastForward: false,
                commitOptions: { subject: args[1] },
            };
        }
        const { branch, fastForward, commitOptions } = options;
        const branchName = typeof branch === "string" ? branch : branch.name;
        const branchLastCommitHash = this._graph.refs.getCommit(branchName);
        if (!branchLastCommitHash) {
            throw new Error(`The branch called "${branchName}" is unknown`);
        }
        let canFastForward = false;
        if (fastForward) {
            const lastCommitHash = this._graph.refs.getCommit(this._branch.name);
            if (lastCommitHash) {
                canFastForward = this._areCommitsConnected(lastCommitHash, branchLastCommitHash);
            }
        }
        if (fastForward && canFastForward) {
            this._fastForwardTo(branchLastCommitHash);
        }
        else {
            this._commitWithParents({
                ...commitOptions,
                subject: (commitOptions && commitOptions.subject) ||
                    `Merge branch ${branchName}`,
            }, [branchLastCommitHash]);
        }
        this._onGraphUpdate();
        return this;
    }
    tag(options) {
        if (this._branch.isDeleted() && !this._isReferenced()) {
            throw new Error(`Cannot tag on the deleted branch "${this.name}"`);
        }
        if (typeof options === "string") {
            this._graph.getUserApi().tag({ name: options, ref: this._branch.name });
        }
        else {
            this._graph.getUserApi().tag({ ...options, ref: this._branch.name });
        }
        return this;
    }
    /**
     * Checkout onto this branch and update "HEAD" in refs
     */
    checkout() {
        if (this._branch.isDeleted() && !this._isReferenced()) {
            throw new Error(`Cannot checkout the deleted branch "${this.name}"`);
        }
        const target = this._branch;
        const headCommit = this._graph.refs.getCommit(target.name);
        this._graph.currentBranch = target;
        // Update "HEAD" in refs when the target branch is not empty
        if (headCommit) {
            this._graph.refs.set("HEAD", headCommit);
        }
        return this;
    }
    // tslint:disable:variable-name - Prefix `_` = explicitly private for JS users
    _commitWithParents(options, parents) {
        const parentOnSameBranch = this._graph.refs.getCommit(this._branch.name);
        if (parentOnSameBranch) {
            parents.unshift(parentOnSameBranch);
        }
        else if (this._branch.parentCommitHash) {
            parents.unshift(this._branch.parentCommitHash);
        }
        const { tag, ...commitOptions } = options;
        const commit = new Commit({
            hash: this._graph.generateCommitHash(),
            author: this._branch.commitDefaultOptions.author || this._graph.author,
            subject: this._branch.commitDefaultOptions.subject ||
                this._graph.commitMessage,
            ...commitOptions,
            parents,
            style: this._getCommitStyle(options.style),
        });
        if (parentOnSameBranch) {
            // Take all the refs from the parent
            const parentRefs = this._graph.refs.getNames(parentOnSameBranch);
            parentRefs.forEach((ref) => this._graph.refs.set(ref, commit.hash));
        }
        else {
            // Set the branch ref
            this._graph.refs.set(this._branch.name, commit.hash);
        }
        // Add the new commit
        this._graph.commits.push(commit);
        // Move HEAD on the last commit
        this.checkout();
        // Add a tag to the commit if `option.tag` is provide
        if (tag)
            this.tag(tag);
    }
    _areCommitsConnected(parentCommitHash, childCommitHash) {
        const childCommit = this._graph.commits.find(({ hash }) => childCommitHash === hash);
        if (!childCommit)
            return false;
        const isFirstCommitOfGraph = childCommit.parents.length === 0;
        if (isFirstCommitOfGraph)
            return false;
        if (childCommit.parents.includes(parentCommitHash)) {
            return true;
        }
        // `childCommitHash` is not a direct child of `parentCommitHash`.
        // But maybe one of `childCommitHash` parent is.
        return childCommit.parents.some((directParentHash) => this._areCommitsConnected(parentCommitHash, directParentHash));
    }
    _fastForwardTo(commitHash) {
        this._graph.refs.set(this._branch.name, commitHash);
    }
    _getCommitStyle(style = {}) {
        return {
            ...withoutUndefinedKeys(this._graph.template.commit),
            ...withoutUndefinedKeys(this._branch.commitDefaultOptions.style),
            ...style,
            message: {
                ...withoutUndefinedKeys(this._graph.template.commit.message),
                ...withoutUndefinedKeys(this._branch.commitDefaultOptions.style.message),
                ...style.message,
                ...withoutUndefinedKeys({
                    display: this._graph.shouldDisplayCommitMessage && undefined,
                }),
            },
            dot: {
                ...withoutUndefinedKeys(this._graph.template.commit.dot),
                ...withoutUndefinedKeys(this._branch.commitDefaultOptions.style.dot),
                ...style.dot,
            },
        };
    }
    _isReferenced() {
        return (this._graph.branches.has(this.name) ||
            this._graph.refs.hasName(this.name) ||
            this._graph.commits
                .reduce((allNames, { refs }) => [...allNames, ...refs], [])
                .includes(this.name));
    }
}
function isBranchMergeOptions(options) {
    return typeof options === "object" && !(options instanceof BranchUserApi);
}

const DELETED_BRANCH_NAME = "";
class Branch {
    constructor(options) {
        this.gitgraph = options.gitgraph;
        this.name = options.name;
        this.style = options.style;
        this.parentCommitHash = options.parentCommitHash;
        this.commitDefaultOptions = options.commitDefaultOptions || { style: {} };
        this.onGraphUpdate = options.onGraphUpdate;
        this.renderLabel = options.renderLabel;
    }
    /**
     * Return the API to manipulate Gitgraph branch as a user.
     */
    getUserApi() {
        return new BranchUserApi(this, this.gitgraph, this.onGraphUpdate);
    }
    /**
     * Return true if branch was deleted.
     */
    isDeleted() {
        return this.name === DELETED_BRANCH_NAME;
    }
}
function createDeletedBranch(gitgraph, style, onGraphUpdate) {
    return new Branch({
        name: DELETED_BRANCH_NAME,
        gitgraph,
        style,
        onGraphUpdate,
    });
}

var Mode;
(function (Mode) {
    Mode["Compact"] = "compact";
})(Mode || (Mode = {}));

class RegularGraphRows {
    constructor(commits) {
        this.rows = new Map();
        this.maxRowCache = undefined;
        this.computeRowsFromCommits(commits);
    }
    getRowOf(commitHash) {
        return this.rows.get(commitHash) || 0;
    }
    getMaxRow() {
        if (this.maxRowCache === undefined) {
            this.maxRowCache = uniq(Array.from(this.rows.values())).length - 1;
        }
        return this.maxRowCache;
    }
    computeRowsFromCommits(commits) {
        commits.forEach((commit, i) => {
            this.rows.set(commit.hash, i);
        });
        this.maxRowCache = undefined;
    }
}
/**
 * Creates a duplicate-free version of an array.
 *
 * Don't use lodash's `uniq` as it increased bundlesize a lot for such a
 * simple function.
 * => The way we bundle for browser seems not to work with `lodash-es`.
 * => I didn't to get tree-shaking to work with `lodash` (the CommonJS version).
 *
 * @param array Array of values
 */
function uniq(array) {
    const set = new Set();
    array.forEach((value) => set.add(value));
    return Array.from(set);
}

class CompactGraphRows extends RegularGraphRows {
    computeRowsFromCommits(commits) {
        commits.forEach((commit, i) => {
            let newRow = i;
            const isFirstCommit = i === 0;
            if (!isFirstCommit) {
                const parentRow = this.getRowOf(commit.parents[0]);
                const historyParent = commits[i - 1];
                newRow = Math.max(parentRow + 1, this.getRowOf(historyParent.hash));
                const isMergeCommit = commit.parents.length > 1;
                if (isMergeCommit) {
                    // Push commit to next row to avoid collision when the branch in which
                    // the merge happens has more commits than the merged branch.
                    const mergeTargetParentRow = this.getRowOf(commit.parents[1]);
                    if (parentRow < mergeTargetParentRow)
                        newRow++;
                }
            }
            this.rows.set(commit.hash, newRow);
        });
    }
}

function createGraphRows(mode, commits) {
    return mode === Mode.Compact
        ? new CompactGraphRows(commits)
        : new RegularGraphRows(commits);
}

/**
 * Assign reusable lanes to branch paths.
 *
 * Branch identity/order stays independent from the assigned lane. Intervals are
 * derived from the graph axis of rendered paths, never from commit timestamps.
 */
class BranchesLanes {
    constructor(branchesPaths, branchesOrder, isGraphVertical, isGraphReverse, getBranchCoordinate) {
        this.lanes = new Map();
        const graphTip = this.getGraphTip(branchesPaths, isGraphVertical, isGraphReverse);
        const intervals = Array.from(branchesPaths).map(([branch, paths]) => {
            const branchCoordinate = getBranchCoordinate(branch.name);
            const points = [];
            paths.forEach((path) => points.push(...path));
            const pointsOnLane = points.filter((point) => {
                const laneCoordinate = isGraphVertical ? point.x : point.y;
                return laneCoordinate === branchCoordinate;
            });
            const graphCoordinates = points.map((point) => isGraphVertical ? point.y : point.x);
            const laneGraphCoordinates = pointsOnLane.map((point) => isGraphVertical ? point.y : point.x);
            const start = Math.min(...graphCoordinates);
            const end = Math.max(...graphCoordinates);
            const interval = {
                branch,
                start,
                end,
                occupiesStart: laneGraphCoordinates.includes(start),
                occupiesEnd: laneGraphCoordinates.includes(end),
                priority: branchesOrder.get(branch.name),
            };
            if (this.isOpenPath(points, pointsOnLane, branchCoordinate, isGraphVertical, isGraphReverse)) {
                interval.start = Math.min(interval.start, graphTip);
                interval.end = Math.max(interval.end, graphTip);
                if (graphTip === interval.start)
                    interval.occupiesStart = true;
                if (graphTip === interval.end)
                    interval.occupiesEnd = true;
            }
            return interval;
        });
        this.assignLanes(intervals);
    }
    /** Return the reusable lane assigned to the given branch. */
    get(branchName) {
        return this.lanes.get(branchName) ?? 0;
    }
    assignLanes(intervals) {
        const sortedIntervals = intervals.sort((a, b) => a.start - b.start || a.priority - b.priority || a.end - b.end);
        const active = [];
        const availableLanes = [];
        const branchesByLane = [];
        sortedIntervals.forEach((interval) => {
            for (let i = active.length - 1; i >= 0; i--) {
                // Intervals are inclusive. Branches may only share a touching row when
                // both paths are on the parent lane there (a merge immediately
                // followed by the next fork), so no node or same-lane segment overlaps.
                const intervalsAreSeparate = active[i].end < interval.start;
                const shareOnlyParentConnection = active[i].end === interval.start &&
                    !active[i].occupiesEnd &&
                    !interval.occupiesStart;
                if (intervalsAreSeparate || shareOnlyParentConnection) {
                    availableLanes.push(active[i].lane);
                    active.splice(i, 1);
                }
            }
            availableLanes.sort((a, b) => a - b);
            const lane = availableLanes.length > 0
                ? availableLanes.shift()
                : branchesByLane.length;
            active.push({
                end: interval.end,
                lane,
                occupiesEnd: interval.occupiesEnd,
            });
            branchesByLane[lane] = branchesByLane[lane] || [];
            branchesByLane[lane].push(interval);
        });
        // Lane allocation above minimizes the lane count. Reorder whole lanes by
        // branch priority afterwards so compareBranchesOrder still controls which
        // side contains its highest-priority branches without breaking reuse.
        const remappedLanes = branchesByLane
            .map((branches, lane) => ({
            lane,
            priority: Math.min(...branches.map(({ priority }) => priority)),
        }))
            .sort((a, b) => a.priority - b.priority || a.lane - b.lane);
        const laneRemap = new Map(remappedLanes.map(({ lane }, remappedLane) => [lane, remappedLane]));
        branchesByLane.forEach((branches, lane) => {
            branches.forEach(({ branch }) => {
                this.lanes.set(branch.name, laneRemap.get(lane));
            });
        });
    }
    isOpenPath(points, pointsOnLane, branchCoordinate, isGraphVertical, isGraphReverse) {
        if (pointsOnLane.length === 0)
            return false;
        const offLanePoints = points.filter((point) => {
            const laneCoordinate = isGraphVertical ? point.x : point.y;
            return laneCoordinate !== branchCoordinate;
        });
        if (offLanePoints.length === 0)
            return true;
        const newestIsMaximum = isGraphVertical ? isGraphReverse : !isGraphReverse;
        const newest = (coordinates) => newestIsMaximum ? Math.max(...coordinates) : Math.min(...coordinates);
        const laneTip = newest(pointsOnLane.map((point) => (isGraphVertical ? point.y : point.x)));
        const connectionTip = newest(offLanePoints.map((point) => (isGraphVertical ? point.y : point.x)));
        return newestIsMaximum ? laneTip > connectionTip : laneTip < connectionTip;
    }
    getGraphTip(branchesPaths, isGraphVertical, isGraphReverse) {
        const coordinates = [];
        branchesPaths.forEach((paths) => {
            paths.forEach((path) => {
                path.forEach((point) => {
                    coordinates.push(isGraphVertical ? point.y : point.x);
                });
            });
        });
        const newestIsMaximum = isGraphVertical ? isGraphReverse : !isGraphReverse;
        return newestIsMaximum
            ? Math.max(...coordinates)
            : Math.min(...coordinates);
    }
}

class BranchesOrder {
    constructor(commits, colors, compareFunction, primaryBranch) {
        this.branches = new Set();
        this.colors = colors;
        commits.forEach((commit) => this.branches.add(commit.branchToDisplay));
        if (primaryBranch && this.branches.has(primaryBranch)) {
            const otherBranches = Array.from(this.branches).filter((branch) => branch !== primaryBranch);
            this.branches = new Set([
                primaryBranch,
                ...(compareFunction
                    ? otherBranches.sort(compareFunction)
                    : otherBranches),
            ]);
        }
        else if (compareFunction) {
            this.branches = new Set(Array.from(this.branches).sort(compareFunction));
        }
    }
    /**
     * Return the order of the given branch name.
     *
     * @param branchName Name of the branch
     */
    get(branchName) {
        return Array.from(this.branches).findIndex((branch) => branch === branchName);
    }
    /**
     * Return the color of the given branch.
     *
     * @param branchName Name of the branch
     */
    getColorOf(branchName) {
        return this.colors[this.get(branchName) % this.colors.length];
    }
}

class Refs {
    constructor() {
        this.commitPerName = new Map();
        this.namesPerCommit = new Map();
    }
    /**
     * Set a new reference to a commit hash.
     *
     * @param name Name of the ref (ex: "master", "v1.0")
     * @param commitHash Commit hash
     */
    set(name, commitHash) {
        const prevCommitHash = this.commitPerName.get(name);
        if (prevCommitHash) {
            this.removeNameFrom(prevCommitHash, name);
        }
        this.addNameTo(commitHash, name);
        this.addCommitTo(name, commitHash);
        return this;
    }
    /**
     * Delete a reference
     *
     * @param name Name of the reference
     */
    delete(name) {
        if (this.hasName(name)) {
            this.removeNameFrom(this.getCommit(name), name);
            this.commitPerName.delete(name);
        }
        return this;
    }
    /**
     * Get the commit hash associated with the given reference name.
     *
     * @param name Name of the ref
     */
    getCommit(name) {
        return this.commitPerName.get(name);
    }
    /**
     * Get the list of reference names associated with given commit hash.
     *
     * @param commitHash Commit hash
     */
    getNames(commitHash) {
        return this.namesPerCommit.get(commitHash) || [];
    }
    /**
     * Get all reference names known.
     */
    getAllNames() {
        return Array.from(this.commitPerName.keys());
    }
    /**
     * Returns true if given commit hash is referenced.
     *
     * @param commitHash Commit hash
     */
    hasCommit(commitHash) {
        return this.namesPerCommit.has(commitHash);
    }
    /**
     * Returns true if given reference name exists.
     *
     * @param name Name of the ref
     */
    hasName(name) {
        return this.commitPerName.has(name);
    }
    removeNameFrom(commitHash, nameToRemove) {
        const names = this.namesPerCommit.get(commitHash) || [];
        this.namesPerCommit.set(commitHash, names.filter((name) => name !== nameToRemove));
    }
    addNameTo(commitHash, nameToAdd) {
        const prevNames = this.namesPerCommit.get(commitHash) || [];
        this.namesPerCommit.set(commitHash, [...prevNames, nameToAdd]);
    }
    addCommitTo(name, commitHashToAdd) {
        this.commitPerName.set(name, commitHashToAdd);
    }
}

/**
 * Calculate branches paths of the graph.
 *
 * It follows the Command pattern:
 * => a class with a single `execute()` public method.
 *
 * Main benefit is we can split computation in smaller steps without
 * passing around parameters (we can rely on private data).
 */
class BranchesPathsCalculator {
    constructor(commits, branches, commitSpacing, isGraphVertical, isGraphReverse, createDeletedBranch) {
        this.branchesPaths = new Map();
        this.commits = commits;
        this.branches = branches;
        this.commitSpacing = commitSpacing;
        this.isGraphVertical = isGraphVertical;
        this.isGraphReverse = isGraphReverse;
        this.createDeletedBranch = createDeletedBranch;
    }
    /**
     * Compute branches paths for graph.
     */
    execute() {
        this.fromCommits();
        this.withMergeCommits();
        return this.smoothBranchesPaths();
    }
    /**
     * Initialize branches paths from calculator's commits.
     */
    fromCommits() {
        this.commits.forEach((commit) => {
            let branch = this.branches.get(commit.branchToDisplay);
            if (!branch) {
                // NB: may not work properly if there are many deleted branches.
                branch = this.getDeletedBranchInPath() || this.createDeletedBranch();
            }
            const path = [];
            const existingBranchPath = this.branchesPaths.get(branch);
            const firstParentCommit = this.commits.find(({ hash }) => hash === commit.parents[0]);
            if (existingBranchPath) {
                path.push(...existingBranchPath);
            }
            else if (firstParentCommit) {
                // Make branch path starts from parent branch (parent commit).
                path.push({ x: firstParentCommit.x, y: firstParentCommit.y });
            }
            path.push({ x: commit.x, y: commit.y });
            this.branchesPaths.set(branch, path);
        });
    }
    /**
     * Insert merge commits points into `branchesPaths`.
     *
     * @example
     *     // Before
     *     [
     *       { x: 0, y: 640 },
     *       { x: 50, y: 560 }
     *     ]
     *
     *     // After
     *     [
     *       { x: 0, y: 640 },
     *       { x: 50, y: 560 },
     *       { x: 50, y: 560, mergeCommit: true }
     *     ]
     */
    withMergeCommits() {
        const mergeCommits = this.commits.filter(({ parents }) => parents.length > 1);
        mergeCommits.forEach((mergeCommit) => {
            const parentOnOriginBranch = this.commits.find(({ hash }) => {
                return hash === mergeCommit.parents[1];
            });
            if (!parentOnOriginBranch)
                return;
            const originBranchName = parentOnOriginBranch.branches
                ? parentOnOriginBranch.branches[0]
                : "";
            let branch = this.branches.get(originBranchName);
            if (!branch) {
                branch = this.getDeletedBranchInPath();
                if (!branch) {
                    // Still no branch? That's strange, we shouldn't set anything.
                    return;
                }
            }
            const lastPoints = [...(this.branchesPaths.get(branch) || [])];
            this.branchesPaths.set(branch, [
                ...lastPoints,
                { x: mergeCommit.x, y: mergeCommit.y, mergeCommit: true },
            ]);
        });
    }
    /**
     * Retrieve deleted branch from calculator's branches paths.
     */
    getDeletedBranchInPath() {
        return Array.from(this.branchesPaths.keys()).find((branch) => branch.isDeleted());
    }
    /**
     * Smooth all paths by putting points on each row.
     */
    smoothBranchesPaths() {
        const branchesPaths = new Map();
        this.branchesPaths.forEach((points, branch) => {
            if (points.length <= 1) {
                branchesPaths.set(branch, [points]);
                return;
            }
            // Cut path on each merge commits
            // Coordinate[] -> Coordinate[][]
            if (this.isGraphVertical) {
                points = points.sort((a, b) => (a.y > b.y ? -1 : 1));
            }
            else {
                points = points.sort((a, b) => (a.x > b.x ? 1 : -1));
            }
            if (this.isGraphReverse) {
                points = points.reverse();
            }
            const paths = points.reduce((mem, point, i) => {
                if (point.mergeCommit) {
                    mem[mem.length - 1].push(pick(point, ["x", "y"]));
                    let j = i - 1;
                    let previousPoint = points[j];
                    // Find the last point which is not a merge
                    while (j >= 0 && previousPoint.mergeCommit) {
                        j--;
                        previousPoint = points[j];
                    }
                    // Start a new array with this point
                    if (j >= 0) {
                        mem.push([previousPoint]);
                    }
                }
                else {
                    mem[mem.length - 1].push(point);
                }
                return mem;
            }, [[]]);
            if (this.isGraphReverse) {
                paths.forEach((path) => path.reverse());
            }
            // Add intermediate points on each sub paths
            if (this.isGraphVertical) {
                paths.forEach((subPath) => {
                    if (subPath.length <= 1)
                        return;
                    const firstPoint = subPath[0];
                    const lastPoint = subPath[subPath.length - 1];
                    const column = subPath[1].x;
                    const branchSize = Math.round(Math.abs(firstPoint.y - lastPoint.y) / this.commitSpacing) - 1;
                    const branchPoints = branchSize > 0
                        ? new Array(branchSize).fill(0).map((_, i) => ({
                            x: column,
                            y: subPath[0].y - this.commitSpacing * (i + 1),
                        }))
                        : [];
                    const lastSubPaths = branchesPaths.get(branch) || [];
                    branchesPaths.set(branch, [
                        ...lastSubPaths,
                        [firstPoint, ...branchPoints, lastPoint],
                    ]);
                });
            }
            else {
                paths.forEach((subPath) => {
                    if (subPath.length <= 1)
                        return;
                    const firstPoint = subPath[0];
                    const lastPoint = subPath[subPath.length - 1];
                    const column = subPath[1].y;
                    const branchSize = Math.round(Math.abs(firstPoint.x - lastPoint.x) / this.commitSpacing) - 1;
                    const branchPoints = branchSize > 0
                        ? new Array(branchSize).fill(0).map((_, i) => ({
                            y: column,
                            x: subPath[0].x + this.commitSpacing * (i + 1),
                        }))
                        : [];
                    const lastSubPaths = branchesPaths.get(branch) || [];
                    branchesPaths.set(branch, [
                        ...lastSubPaths,
                        [firstPoint, ...branchPoints, lastPoint],
                    ]);
                });
            }
        });
        return branchesPaths;
    }
}
/**
 * Return a string ready to use in `svg.path.d` from coordinates
 *
 * @param coordinates Collection of coordinates
 */
function toSvgPath(coordinates, isBezier, isVertical) {
    return coordinates
        .map((path) => "M" +
        path
            .map(({ x, y }, i, points) => {
            if (isBezier &&
                points.length > 1 &&
                (i === 1 || i === points.length - 1)) {
                const previous = points[i - 1];
                if (isVertical) {
                    const middleY = (previous.y + y) / 2;
                    return `C ${previous.x} ${middleY} ${x} ${middleY} ${x} ${y}`;
                }
                else {
                    const middleX = (previous.x + x) / 2;
                    return `C ${middleX} ${previous.y} ${middleX} ${y} ${x} ${y}`;
                }
            }
            return `L ${x} ${y}`;
        })
            .join(" ")
            .slice(1))
        .join(" ");
}

/**
 * Associate every commit with the branch tips that can reach it through
 * first-parent ancestry.
 */
function getBranches(commits, refs) {
    const result = new Map();
    const commitsByHash = new Map(commits.map((commit) => [commit.hash, commit]));
    const branches = refs.getAllNames().filter((name) => name !== "HEAD");
    branches.forEach((branch) => {
        let currentHash = refs.getCommit(branch);
        const visited = new Set();
        while (currentHash && !visited.has(currentHash)) {
            visited.add(currentHash);
            const commitBranches = result.get(currentHash) || new Set();
            commitBranches.add(branch);
            result.set(currentHash, commitBranches);
            currentHash = commitsByHash.get(currentHash)?.parents[0];
        }
    });
    return result;
}
/**
 * Return the commits that form the configured primary branch's first-parent
 * history. Missing refs and truncated parents stop traversal safely.
 */
function getPrimaryBranchFirstParentHistory(commits, refs, primaryBranch) {
    const history = new Set();
    if (!primaryBranch)
        return history;
    const commitsByHash = new Map(commits.map((commit) => [commit.hash, commit]));
    let currentHash = refs.getCommit(primaryBranch);
    while (currentHash && !history.has(currentHash)) {
        const commit = commitsByHash.get(currentHash);
        if (!commit)
            break;
        history.add(currentHash);
        currentHash = commit.parents[0];
    }
    return history;
}
/** Add inferred branch ownership to a commit without changing its real refs. */
function withBranches(branches, commit, primaryBranch, primaryBranchFirstParentHistory) {
    let commitBranches = Array.from(branches.get(commit.hash) || []);
    if (primaryBranch &&
        primaryBranchFirstParentHistory.has(commit.hash) &&
        commitBranches.includes(primaryBranch)) {
        commitBranches = [
            primaryBranch,
            ...commitBranches.filter((branch) => branch !== primaryBranch),
        ];
    }
    if (commitBranches.length === 0) {
        // No branch => branch has been deleted.
        commitBranches = [DELETED_BRANCH_NAME];
    }
    return commit.setBranches(commitBranches);
}

class GitgraphUserApi {
    // tslint:enable:variable-name
    constructor(graph, onGraphUpdate) {
        this._graph = graph;
        this._onGraphUpdate = onGraphUpdate;
    }
    /**
     * Clear everything (as `rm -rf .git && git init`).
     */
    clear() {
        this._graph.refs = new Refs();
        this._graph.tags = new Refs();
        this._graph.commits = [];
        this._graph.branches = new Map();
        this._graph.currentBranch = this._graph.createBranch("master");
        this._onGraphUpdate();
        return this;
    }
    commit(options) {
        this._graph.currentBranch.getUserApi().commit(options);
        return this;
    }
    branch(args) {
        return this._graph.createBranch(args).getUserApi();
    }
    tag(...args) {
        // Deal with shorter syntax
        let name;
        let ref;
        let style;
        let render;
        if (typeof args[0] === "string") {
            name = args[0];
            ref = args[1];
        }
        else {
            name = args[0].name;
            ref = args[0].ref;
            style = args[0].style;
            render = args[0].render;
        }
        if (!ref) {
            const head = this._graph.refs.getCommit("HEAD");
            if (!head)
                return this;
            ref = head;
        }
        let commitHash;
        if (this._graph.refs.hasCommit(ref)) {
            // `ref` is a `Commit["hash"]`
            commitHash = ref;
        }
        if (this._graph.refs.hasName(ref)) {
            // `ref` is a `Branch["name"]`
            commitHash = this._graph.refs.getCommit(ref);
        }
        if (!commitHash) {
            throw new Error(`The ref "${ref}" does not exist`);
        }
        this._graph.tags.set(name, commitHash);
        this._graph.tagStyles[name] = style;
        this._graph.tagRenders[name] = render;
        this._onGraphUpdate();
        return this;
    }
    /**
     * Import a JSON.
     *
     * Data can't be typed since it comes from a JSON.
     * We validate input format and throw early if something is invalid.
     *
     * @experimental
     * @param data JSON from `git2json` output
     */
    import(data) {
        const invalidData = new Error("Only `git2json` format is supported for imported data.");
        // We manually validate input data instead of using a lib like yup.
        // => this is to keep bundlesize small.
        if (!Array.isArray(data)) {
            throw invalidData;
        }
        const areDataValid = data.every((options) => {
            return (typeof options === "object" &&
                typeof options.author === "object" &&
                Array.isArray(options.refs));
        });
        if (!areDataValid) {
            throw invalidData;
        }
        const commitOptionsList = data
            .map((options) => ({
            ...options,
            style: {
                ...this._graph.template.commit,
                message: {
                    ...this._graph.template.commit.message,
                    display: this._graph.shouldDisplayCommitMessage,
                },
            },
            author: `${options.author.name} <${options.author.email}>`,
        }))
            // Git2json outputs is reverse-chronological.
            // We need to commit it chronological order.
            .reverse();
        // Use validated `value`.
        this.clear();
        this._graph.commits = commitOptionsList.map((options) => new Commit(options));
        // Create tags & refs.
        commitOptionsList.forEach(({ refs, hash }) => {
            if (!refs)
                return;
            if (!hash)
                return;
            const TAG_PREFIX = "tag: ";
            const tags = refs
                .map((ref) => ref.split(TAG_PREFIX))
                .map(([, tag]) => tag)
                .filter((tag) => typeof tag === "string");
            tags.forEach((tag) => this._graph.tags.set(tag, hash));
            refs
                .filter((ref) => !ref.startsWith(TAG_PREFIX))
                .forEach((ref) => this._graph.refs.set(ref, hash));
        });
        // Create branches.
        const branches = getBranches(this._graph.commits, this._graph.refs);
        const primaryBranchFirstParentHistory = getPrimaryBranchFirstParentHistory(this._graph.commits, this._graph.refs, this._graph.primaryBranch);
        this._graph.commits
            .map((commit) => withBranches(branches, commit, this._graph.primaryBranch, primaryBranchFirstParentHistory))
            .reduce((mem, commit) => {
            if (!commit.branches)
                return mem;
            commit.branches.forEach((branch) => mem.add(branch));
            return mem;
        }, new Set())
            .forEach((branch) => this.branch(branch));
        this._onGraphUpdate();
        return this;
    }
}

class GitgraphCore {
    get isHorizontal() {
        return (this.orientation === Orientation.Horizontal ||
            this.orientation === Orientation.HorizontalReverse);
    }
    get isVertical() {
        return !this.isHorizontal;
    }
    get isReverse() {
        return (this.orientation === Orientation.HorizontalReverse ||
            this.orientation === Orientation.VerticalReverse);
    }
    get shouldDisplayCommitMessage() {
        return !this.isHorizontal && this.mode !== Mode.Compact;
    }
    constructor(options = {}) {
        this.refs = new Refs();
        this.tags = new Refs();
        this.tagStyles = {};
        this.tagRenders = {};
        this.commits = [];
        this.branches = new Map();
        this.listeners = [];
        this.nextTimeoutId = null;
        this.template = getTemplate(options.template);
        // Set a default `master` branch
        this.currentBranch = this.createBranch("master");
        // Set all options with default values
        this.orientation = options.orientation;
        this.reverseArrow = booleanOptionOr(options.reverseArrow, false);
        this.initCommitOffsetX = numberOptionOr(options.initCommitOffsetX, 0);
        this.initCommitOffsetY = numberOptionOr(options.initCommitOffsetY, 0);
        this.mode = options.mode;
        this.author = options.author || "Sergio Flores <saxo-guy@epic.com>";
        this.commitMessage =
            options.commitMessage || "He doesn't like George Michael! Boooo!";
        this.generateCommitHash =
            typeof options.generateCommitHash === "function"
                ? options.generateCommitHash
                : () => undefined;
        this.branchesOrderFunction =
            typeof options.compareBranchesOrder === "function"
                ? options.compareBranchesOrder
                : undefined;
        this.primaryBranch = options.primaryBranch;
        this.branchLabelOnEveryCommit = booleanOptionOr(options.branchLabelOnEveryCommit, false);
    }
    /**
     * Return the API to manipulate Gitgraph as a user.
     * Rendering library should give that API to their consumer.
     */
    getUserApi() {
        return new GitgraphUserApi(this, () => this.next());
    }
    /**
     * Add a change listener.
     * It will be called any time the graph have changed (commit, merge…).
     *
     * @param listener A callback to be invoked on every change.
     * @returns A function to remove this change listener.
     */
    subscribe(listener) {
        this.listeners.push(listener);
        let isSubscribed = true;
        return () => {
            if (!isSubscribed)
                return;
            isSubscribed = false;
            const index = this.listeners.indexOf(listener);
            this.listeners.splice(index, 1);
        };
    }
    /**
     * Return all data required for rendering.
     * Rendering libraries will use this to implement their rendering strategy.
     */
    getRenderedData() {
        const commits = this.computeRenderedCommits();
        const branchesPaths = this.computeRenderedBranchesPaths(commits);
        const commitMessagesX = this.computeCommitMessagesX(branchesPaths);
        this.computeBranchesColor(commits, branchesPaths);
        return { commits, branchesPaths, commitMessagesX };
    }
    createBranch(args) {
        const defaultParentBranchName = "HEAD";
        let options = {
            gitgraph: this,
            name: "",
            parentCommitHash: this.refs.getCommit(defaultParentBranchName),
            style: this.template.branch,
            onGraphUpdate: () => this.next(),
        };
        if (typeof args === "string") {
            options.name = args;
            options.parentCommitHash = this.refs.getCommit(defaultParentBranchName);
        }
        else {
            const parentBranchName = args.from
                ? args.from.name
                : defaultParentBranchName;
            const parentCommitHash = this.refs.getCommit(parentBranchName) ||
                (this.refs.hasCommit(args.from) ? args.from : undefined);
            args.style = args.style || {};
            options = {
                ...options,
                ...args,
                parentCommitHash,
                style: {
                    ...options.style,
                    ...args.style,
                    label: {
                        ...options.style.label,
                        ...args.style.label,
                    },
                },
            };
        }
        const branch = new Branch(options);
        this.branches.set(branch.name, branch);
        return branch;
    }
    /**
     * Return commits with data for rendering.
     */
    computeRenderedCommits() {
        const branches = getBranches(this.commits, this.refs);
        const primaryBranchFirstParentHistory = getPrimaryBranchFirstParentHistory(this.commits, this.refs, this.primaryBranch);
        // Commits that are not associated to a branch in `branches`
        // were in a deleted branch. If the latter was merged beforehand
        // they are reachable and are rendered. Others are not
        const reachableUnassociatedCommits = (() => {
            const unassociatedCommits = new Set(this.commits.reduce((commits, { hash }) => !branches.has(hash) ? [...commits, hash] : commits, []));
            const tipsOfMergedBranches = this.commits.reduce((tipsOfMergedBranches, commit) => commit.parents.length > 1
                ? [
                    ...tipsOfMergedBranches,
                    ...commit.parents
                        .slice(1)
                        .map((parentHash) => this.commits.find(({ hash }) => parentHash === hash)),
                ]
                : tipsOfMergedBranches, []);
            const reachableCommits = new Set();
            tipsOfMergedBranches.forEach((tip) => {
                let currentCommit = tip;
                while (currentCommit && unassociatedCommits.has(currentCommit.hash)) {
                    reachableCommits.add(currentCommit.hash);
                    currentCommit =
                        currentCommit.parents.length > 0
                            ? this.commits.find(({ hash }) => currentCommit.parents[0] === hash)
                            : undefined;
                }
            });
            return reachableCommits;
        })();
        const commitsToRender = this.commits.filter(({ hash }) => branches.has(hash) || reachableUnassociatedCommits.has(hash));
        const commitsWithBranches = commitsToRender.map((commit) => withBranches(branches, commit, this.primaryBranch, primaryBranchFirstParentHistory));
        const rows = createGraphRows(this.mode, commitsToRender);
        const branchesOrder = new BranchesOrder(commitsWithBranches, this.template.colors, this.branchesOrderFunction, this.primaryBranch);
        const commitsWithPreliminaryPositions = commitsWithBranches
            .map((commit) => commit.setRefs(this.refs))
            .map((commit) => this.withPosition(rows, branchesOrder.get.bind(branchesOrder), commit));
        const preliminaryBranchesPaths = this.computeRenderedBranchesPaths(commitsWithPreliminaryPositions);
        const branchesLanes = new BranchesLanes(preliminaryBranchesPaths, branchesOrder, this.isVertical, this.isReverse, (branchName) => this.getBranchCoordinate(branchesOrder.get(branchName)));
        return (commitsWithPreliminaryPositions
            .map((commit) => this.withPosition(rows, branchesLanes.get.bind(branchesLanes), commit))
            // Fallback commit computed color on branch color.
            .map((commit) => commit.withDefaultColor(this.getBranchDefaultColor(branchesOrder, commit.branchToDisplay)))
            // Tags need commit style to be computed (with default color).
            .map((commit) => commit.setTags(this.tags, (name) => Object.assign({}, this.tagStyles[name], this.template.tag), (name) => this.tagRenders[name])));
    }
    /**
     * Return branches paths with all data required for rendering.
     *
     * @param commits List of commits with rendering data computed
     */
    computeRenderedBranchesPaths(commits) {
        return new BranchesPathsCalculator(commits, this.branches, this.template.commit.spacing, this.isVertical, this.isReverse, () => createDeletedBranch(this, this.template.branch, () => this.next())).execute();
    }
    /**
     * Set branches colors based on branches paths.
     *
     * @param commits List of graph commits
     * @param branchesPaths Branches paths to be rendered
     */
    computeBranchesColor(commits, branchesPaths) {
        const branchesOrder = new BranchesOrder(commits, this.template.colors, this.branchesOrderFunction, this.primaryBranch);
        Array.from(branchesPaths).forEach(([branch]) => {
            branch.computedColor =
                branch.style.color ||
                    this.getBranchDefaultColor(branchesOrder, branch.name);
        });
    }
    /**
     * Return commit messages X position for rendering.
     *
     * @param branchesPaths Branches paths to be rendered
     */
    computeCommitMessagesX(branchesPaths) {
        const laneCoordinates = [];
        branchesPaths.forEach((paths) => {
            paths.forEach((path) => {
                path.forEach((point) => {
                    laneCoordinates.push(this.isVertical ? point.x : point.y);
                });
            });
        });
        if (laneCoordinates.length === 0)
            return 0;
        if (this.template.branch.spacing === 0)
            return 0;
        const laneOffset = this.isVertical
            ? this.initCommitOffsetX
            : this.initCommitOffsetY;
        const maxLaneCoordinate = Math.max(...laneCoordinates);
        const maxLane = Math.round((maxLaneCoordinate - laneOffset) / this.template.branch.spacing);
        return (maxLane + 1) * this.template.branch.spacing;
    }
    /**
     * Add position to given commit.
     *
     * @param rows Graph rows
     * @param getBranchLane Return the computed lane of a branch
     * @param commit Commit to position
     */
    withPosition(rows, getBranchLane, commit) {
        const row = rows.getRowOf(commit.hash);
        const maxRow = rows.getMaxRow();
        const lane = getBranchLane(commit.branchToDisplay);
        switch (this.orientation) {
            default:
                return commit.setPosition({
                    x: this.initCommitOffsetX + this.template.branch.spacing * lane,
                    y: this.initCommitOffsetY +
                        this.template.commit.spacing * (maxRow - row),
                });
            case Orientation.VerticalReverse:
                return commit.setPosition({
                    x: this.initCommitOffsetX + this.template.branch.spacing * lane,
                    y: this.initCommitOffsetY + this.template.commit.spacing * row,
                });
            case Orientation.Horizontal:
                return commit.setPosition({
                    x: this.initCommitOffsetX + this.template.commit.spacing * row,
                    y: this.initCommitOffsetY + this.template.branch.spacing * lane,
                });
            case Orientation.HorizontalReverse:
                return commit.setPosition({
                    x: this.initCommitOffsetX +
                        this.template.commit.spacing * (maxRow - row),
                    y: this.initCommitOffsetY + this.template.branch.spacing * lane,
                });
        }
    }
    getBranchCoordinate(lane) {
        const laneOffset = this.isVertical
            ? this.initCommitOffsetX
            : this.initCommitOffsetY;
        return laneOffset + this.template.branch.spacing * lane;
    }
    /**
     * Return the default color for given branch.
     *
     * @param branchesOrder Computed order of branches
     * @param branchName Name of the branch
     */
    getBranchDefaultColor(branchesOrder, branchName) {
        return branchesOrder.getColorOf(branchName);
    }
    /**
     * Tell each listener something new happened.
     * E.g. a rendering library will know it needs to re-render the graph.
     */
    next() {
        if (this.nextTimeoutId) {
            globalThis.clearTimeout(this.nextTimeoutId);
        }
        // Use setTimeout() with `0` to debounce call to next tick.
        this.nextTimeoutId = globalThis.setTimeout(() => {
            this.listeners.forEach((listener) => listener(this.getRenderedData()));
        }, 0);
    }
}

export { Branch, BranchUserApi, Commit, GitgraphCore, GitgraphUserApi, MergeStyle, Mode, Orientation, Refs, Tag, TemplateName, arrowSvgPath, templateExtend, toSvgPath };
//# sourceMappingURL=index.mjs.map
